import React from 'react';
import { Layout } from 'antd';
import { connect } from 'react-redux';
import { workOrderPrioritiesRestCrudThunks } from '../../thunks/work_order_priorities_thunks';
import LifeCycleAnalyticsOverviewHeader from './LifeCycleAnalyticsOverviewHeader';
import LifeCycleAnalyticsOverviewDashboard from './LifeCycleAnalyticsOverviewDashboard';
import {
	fetchLifecycleMonthlyApprovalTime,
	fetchLifecycleMonthlyResponseTime,
	fetchLifecycleMonthlyResolutionTime,
	fetchLifecycleMonthlyFirstTimeFixRate,
	fetchLifecycleMonthlySpend,
	fetchLifecycleOverallSummary,
	fetchLifecyclePrioritySummary,
	fetchLifecycleSupplierPerformance,
} from '../../thunks/lifecycle_analytics_thunks';
import { clearNavbarState, updateNavbarState } from '../../actions/navbar_actions';
import { useLifeCycleAnalyticsOverview } from './useLifeCycleAnalyticsOverview';
import '../charts/chartTheme';
import './registerTopPerformerShape';
import './LifeCycleAnalyticsOverviewPage.less';

const { Content } = Layout;

/** Literal Tailwind tokens for print — keep as quoted strings so JIT keeps every rule. */
const LIFE_CYCLE_OVERVIEW_CONTENT_CLASSNAME = [
	'lifeCycleAnalyticsOverviewPage',
	'[page:life-cycle-analytics]',
	'print:!h-auto',
	'print:!min-h-0',
	'print:!max-h-none',
	'print:!overflow-visible',
	'print:[print-color-adjust:exact]',
	'print:[-webkit-print-color-adjust:exact]',
	'print:[&_.ant-table-wrapper]:!max-h-none',
	'print:[&_.ant-table-wrapper]:!h-auto',
	'print:[&_.ant-table-wrapper]:!overflow-visible',
	'print:[&_.ant-table-wrapper]:!overflow-x-visible',
	'print:[&_.ant-table-wrapper]:!overflow-y-visible',
	'print:[&_.ant-table]:!max-h-none',
	'print:[&_.ant-table]:!h-auto',
	'print:[&_.ant-table]:!overflow-visible',
	'print:[&_.ant-table]:!overflow-x-visible',
	'print:[&_.ant-table]:!overflow-y-visible',
	'print:[&_.ant-table-container]:!max-h-none',
	'print:[&_.ant-table-container]:!h-auto',
	'print:[&_.ant-table-container]:!overflow-visible',
	'print:[&_.ant-table-container]:!overflow-x-visible',
	'print:[&_.ant-table-container]:!overflow-y-visible',
	'print:[&_.ant-table-content]:!max-h-none',
	'print:[&_.ant-table-content]:!h-auto',
	'print:[&_.ant-table-content]:!overflow-visible',
	'print:[&_.ant-table-content]:!overflow-x-visible',
	'print:[&_.ant-table-content]:!overflow-y-visible',
	'print:[&_.ant-table-scroll]:!max-h-none',
	'print:[&_.ant-table-scroll]:!h-auto',
	'print:[&_.ant-table-scroll]:!overflow-visible',
	'print:[&_.ant-table-scroll]:!overflow-x-visible',
	'print:[&_.ant-table-scroll]:!overflow-y-visible',
	'print:[&_.ant-table-body]:!max-h-none',
	'print:[&_.ant-table-body]:!h-auto',
	'print:[&_.ant-table-body]:!overflow-visible',
	'print:[&_.ant-table-body]:!overflow-x-visible',
	'print:[&_.ant-table-body]:!overflow-y-visible',
	'print:[&_.ant-table-body-inner]:!max-h-none',
	'print:[&_.ant-table-body-inner]:!h-auto',
	'print:[&_.ant-table-body-inner]:!overflow-visible',
	'print:[&_.ant-table-body-inner]:!overflow-x-visible',
	'print:[&_.ant-table-body-inner]:!overflow-y-visible',
	'print:[&_.rc-table]:!max-h-none',
	'print:[&_.rc-table]:!h-auto',
	'print:[&_.rc-table]:!overflow-visible',
	'print:[&_.rc-table]:!overflow-x-visible',
	'print:[&_.rc-table]:!overflow-y-visible',
	'print:[&_.rc-table-container]:!max-h-none',
	'print:[&_.rc-table-container]:!h-auto',
	'print:[&_.rc-table-container]:!overflow-visible',
	'print:[&_.rc-table-container]:!overflow-x-visible',
	'print:[&_.rc-table-container]:!overflow-y-visible',
	'print:[&_.rc-table-body]:!max-h-none',
	'print:[&_.rc-table-body]:!h-auto',
	'print:[&_.rc-table-body]:!overflow-visible',
	'print:[&_.rc-table-body]:!overflow-x-visible',
	'print:[&_.rc-table-body]:!overflow-y-visible',
	'print:[&_.ant-table-thead>tr>th]:!static',
	'print:[&_.ant-spin-nested-loading]:!overflow-visible',
	'print:[&_.ant-spin-nested-loading]:!max-h-none',
	'print:[&_.ant-spin-nested-loading]:!h-auto',
	'print:[&_.ant-spin-container]:!overflow-visible',
	'print:[&_.ant-spin-container]:!max-h-none',
	'print:[&_.ant-spin-container]:!h-auto',
	'print:[&_.ant-card]:!overflow-visible',
	'print:[&_.ant-card-body]:!overflow-visible',
	'print:[&_canvas]:!max-w-full',
	'print:[&_.ant-card-body>div]:!overflow-visible',
	'print:[&_.ant-card-body>div]:!max-h-none',
	'print:[&_.ant-col]:!overflow-visible',
].join(' ');

const LifeCycleAnalyticsOverviewPage = (props) => {
	const { companyConfig, header, dashboard } = useLifeCycleAnalyticsOverview(props);
	return (
		<Content className={LIFE_CYCLE_OVERVIEW_CONTENT_CLASSNAME}>
			<LifeCycleAnalyticsOverviewHeader companyConfig={companyConfig} {...header} />
			<LifeCycleAnalyticsOverviewDashboard {...dashboard} />
		</Content>
	);
};

const mapStateToProps = (state) => ({
	userType: (state as any).session.userType,
	currentUser: (state as any).session.currentUser,
	companyConfig: state.company_config,
	workOrderPriorities: state.work_order_priorities,
});

const mapDispatchToProps = (dispatch) => ({ dispatch });

const mergeProps = (stateProps, dispatchProps, ownProps) => {
	const { userType } = stateProps;
	const { dispatch } = dispatchProps;
	return {
		...ownProps,
		...stateProps,
		fetchPrioritySummary: (params, filters = null, pagination = null, sorting = null) =>
			dispatch(fetchLifecyclePrioritySummary(userType)(params, filters, pagination, sorting)),
		fetchOverallSummary: (params, filters = null, pagination = null, sorting = null) =>
			dispatch(fetchLifecycleOverallSummary(userType)(params, filters, pagination, sorting)),
		fetchSupplierPerformance: (params, filters = null, pagination = null, sorting = null) =>
			dispatch(fetchLifecycleSupplierPerformance(userType)(params, filters, pagination, sorting)),
		fetchMonthlyApprovalTime: (params, filters = null, pagination = null, sorting = null) =>
			dispatch(fetchLifecycleMonthlyApprovalTime(userType)(params, filters, pagination, sorting)),
		fetchMonthlyResponseTime: (params, filters = null, pagination = null, sorting = null) =>
			dispatch(fetchLifecycleMonthlyResponseTime(userType)(params, filters, pagination, sorting)),
		fetchMonthlyResolutionTime: (params, filters = null, pagination = null, sorting = null) =>
			dispatch(fetchLifecycleMonthlyResolutionTime(userType)(params, filters, pagination, sorting)),
		fetchMonthlyFirstTimeFixRate: (params, filters = null, pagination = null, sorting = null) =>
			dispatch(
				fetchLifecycleMonthlyFirstTimeFixRate(userType)(params, filters, pagination, sorting)
			),
		fetchMonthlySpend: (params, filters = null, pagination = null, sorting = null) =>
			dispatch(fetchLifecycleMonthlySpend(userType)(params, filters, pagination, sorting)),
		fetchWorkOrderPriorities: () => dispatch(workOrderPrioritiesRestCrudThunks(userType).read({})),
		updateNavbar: (backlinkTitle, backlinkTo, navbarBackgroundColor) =>
			dispatch(updateNavbarState(backlinkTitle, backlinkTo, navbarBackgroundColor)),
		clearNavbar: () => dispatch(clearNavbarState()),
	};
};

export default connect(
	mapStateToProps,
	mapDispatchToProps,
	mergeProps
)(LifeCycleAnalyticsOverviewPage);
